import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { SearchSuggestions } from "./SearchSuggestions";
import { MonitorPlay, LogOut, User, Search, Menu, X, Gift } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { to: "/", label: "Home" },
    { to: "/channels", label: "Channels" },
    { to: "/about", label: "About" },
  ];
  if (user) links.push({ to: "/my-list", label: "My List" });
  if (user) links.push({ to: "/dashboard", label: "Continue Watching" });
  if (user?.role === "admin") links.push({ to: "/admin", label: "Admin" });

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setMobileOpen(false);
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const navKey = (label) => `nav-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <nav
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-2xl border-b border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group shrink-0">
          <MonitorPlay className="w-6 h-6 text-[#FF2A2A]" strokeWidth={2.5} />
          <span className="font-display font-900 text-xl tracking-tighter uppercase">
            QTV<span className="text-[#FF2A2A]">Network</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={navKey(l.label)}
              className={`text-sm tracking-wide transition-colors whitespace-nowrap ${
                pathname === l.to ? "text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {/* Desktop expanding search */}
          <div className="hidden md:flex items-center">
            {searchOpen ? (
              <form onSubmit={submitSearch} className="relative flex items-center">
                <input
                  data-testid="nav-search-input"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onBlur={() => !query && setSearchOpen(false)}
                  placeholder="Search…"
                  className="w-56 bg-white/10 border border-white/20 focus:border-[#FF2A2A] rounded-sm px-3 py-1.5 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors"
                />
                <SearchSuggestions
                  query={query}
                  testid="nav-search-suggestions"
                  className="right-0 w-80"
                  onSelect={() => {
                    setSearchOpen(false);
                    setQuery("");
                  }}
                />
              </form>
            ) : (
              <button
                data-testid="nav-search-toggle"
                onClick={() => setSearchOpen(true)}
                aria-label="Search"
                className="text-zinc-300 hover:text-white transition-colors"
              >
                <Search className="w-5 h-5" />
              </button>
            )}
          </div>

          {user ? (
            <>
              <Link
                to="/refer"
                data-testid="nav-invite"
                className="hidden md:flex items-center gap-1.5 text-sm text-[#FF2A2A] hover:text-[#FF4D4D] transition-colors font-medium"
              >
                <Gift className="w-4 h-4" /> Invite
              </Link>
              <Link
                to="/dashboard"
                className="hidden sm:flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"
              >
                <User className="w-4 h-4" /> {user.name}
              </Link>
              <button
                data-testid="logout-button"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="hidden md:flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4" /> Exit
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login"
                className="hidden sm:block text-sm text-zinc-300 hover:text-white transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="hidden sm:block text-sm bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white px-4 py-2 rounded-sm transition-colors font-medium"
              >
                Join Free
              </Link>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Open menu"
            className="md:hidden text-white"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-2xl px-6 py-5 space-y-4"
        >
          <form onSubmit={submitSearch} className="relative">
            <Search className="absolute left-3 top-[1.15rem] -translate-y-1/2 w-4 h-4 text-zinc-500 z-10" />
            <input
              data-testid="mobile-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search shows, creators…"
              className="w-full bg-white/10 border border-white/20 focus:border-[#FF2A2A] rounded-sm pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors"
            />
            <SearchSuggestions
              query={query}
              testid="mobile-search-suggestions"
              className="left-0 right-0"
              onSelect={() => {
                setMobileOpen(false);
                setQuery("");
              }}
            />
          </form>

          <div className="flex flex-col">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-${navKey(l.label)}`}
                className={`py-2.5 text-base border-b border-white/5 ${
                  pathname === l.to ? "text-white" : "text-zinc-300"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="pt-1">
            {user ? (
              <div className="flex flex-col gap-4">
                <Link
                  to="/refer"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-invite"
                  className="flex items-center gap-2 text-sm text-[#FF2A2A] font-medium"
                >
                  <Gift className="w-4 h-4" /> Invite Friends
                </Link>
                <button
                  data-testid="mobile-logout-button"
                  onClick={() => {
                    logout();
                    setMobileOpen(false);
                    navigate("/");
                  }}
                  className="flex items-center gap-2 text-sm text-zinc-400"
                >
                  <LogOut className="w-4 h-4" /> Sign out ({user.name})
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-login"
                  className="flex-1 text-center text-sm border border-white/20 text-white px-4 py-2.5 rounded-sm"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-register"
                  className="flex-1 text-center text-sm bg-[#FF2A2A] text-white px-4 py-2.5 rounded-sm font-medium"
                >
                  Join Free
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
